import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useQuery } from '@tanstack/react-query';
import { getReport } from 'services/report/getReport';



export default function ProgressTracker() {
  // --- Backend-driven state ---
  const coachId = localStorage.getItem('userid');
  const { data, isLoading, error } = useQuery({
    enabled: !!coachId,
    queryKey: ['reports', coachId],
    queryFn: () => getReport({ coachId: Number(coachId) }),
  });
  const allReports = data?.data || [];

  // Extract all months present in the data
  const monthsSet = new Set();
  allReports.forEach((r) => {
    const d = new Date(r.date);
    monthsSet.add(`${d.getFullYear()}-${d.getMonth()}`); // unique per year/month
  });
  // Map to readable month names (e.g. 'January 2025')
  const monthKeyToLabel = {};
  monthsSet.forEach((key) => {
    const [year, monthIdx] = key.split('-');
    const date = new Date(year, monthIdx);
    monthKeyToLabel[key] = `${date.toLocaleString('default', { month: 'long' })} ${year}`;
  });
  const availableMonths = Array.from(monthsSet).sort().map((key) => monthKeyToLabel[key]);

  // Default to current month if available, else first available
  const currentMonthKey = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth()}`;
  })();
  const currentMonthLabel = monthKeyToLabel[currentMonthKey] || availableMonths[0] || '';
  const [selectedMonth, setSelectedMonth] = useState(currentMonthLabel);
  const [viewType, setViewType] = useState('weekly');

  // Helper: get month key from label
  function getMonthKeyFromLabel(label) {
    for (const [key, val] of Object.entries(monthKeyToLabel)) {
      if (val === label) return key;
    }
    return null;
  }

  // Group reports by student and by selected month
  const monthKey = getMonthKeyFromLabel(selectedMonth);
  const [selYear, selMonthIdx] = monthKey ? monthKey.split('-').map(Number) : [null, null];
  // Get days in selected month
  const daysInMonth = selYear && selMonthIdx !== null ? new Date(selYear, selMonthIdx + 1, 0).getDate() : 0;
  // Week ranges: [start, end] (1-indexed)
  const weekRanges = [
    [1, 7],
    [8, 14],
    [15, 21],
    [22, 28],
    [29, daysInMonth],
  ];

  // Build student data
  const studentsMap = {};
  allReports.forEach((r) => {
    const d = new Date(r.date);
    const rMonth = d.getMonth();
    const rYear = d.getFullYear();
    if (rMonth !== selMonthIdx || rYear !== selYear) return;
    if (!studentsMap[r.studentName]) {
      studentsMap[r.studentName] = { weeks: [[], [], [], [], []], all: [] };
    }
    const day = d.getDate();
    // Find week index
    let weekIdx = weekRanges.findIndex(([start, end]) => day >= start && day <= end);
    if (weekIdx === -1) weekIdx = 0;
    studentsMap[r.studentName].weeks[weekIdx].push(r.performance);
    studentsMap[r.studentName].all.push(r.performance);
  });

  // Prepare students array for table
  const students = Object.entries(studentsMap).map(([studentName, { weeks, all }]) => {
    // For each week, average or '-'
    const weekAverages = weeks.map((arr) =>
      arr.length > 0 ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1) + '%' : '-'
    );
    // Monthly average
    const monthAvg = all.length > 0 ? (all.reduce((a, b) => a + b, 0) / daysInMonth).toFixed(1) + '%' : '-';
    return { studentName, weekAverages, monthAvg };
  });

  // Table week labels (dynamic for 4 or 5 weeks)
  const weekLabels = weekRanges
    .map(([start, end], i) => (end >= start ? `week_${i + 1}` : null))
    .filter(Boolean);

  return (
    <MainCard>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={(_, newValue) => {
            if (newValue) setViewType(newValue);
          }}
          size="small"
        >
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
        {viewType === 'weekly' && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {availableMonths.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 600 }} aria-label="progress table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              {viewType === 'weekly'
                ? weekLabels.map((week, idx) => (
                    <TableCell key={week} align="center">{`Week ${idx + 1}`}</TableCell>
                  ))
                : null}
              <TableCell align="center">Avg Accuracy</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.length > 0 ? (
              students.map((student, index) => (
                <TableRow key={student.studentName}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{student.studentName}</TableCell>
                  {viewType === 'weekly'
                    ? student.weekAverages.map((avg, idx) => (
                        <TableCell key={idx} align="center">{avg}</TableCell>
                      ))
                    : null}
                  <TableCell align="center">{student.monthAvg}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={viewType === 'weekly' ? weekLabels.length + 3 : 3} align="center">
                  No data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );
}
